import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DeprecatedConfirmationDialogComponent } from '@app/components';
import { FeatureFlag } from '@app/enums';
import { Chippable } from '@app/interfaces';
import { JobAttachment } from '@app/models/recruiting/job-attachments.model';
import { JobAttachmentTypes } from '@app/modules/applicant-tracker/enums/job-attachment-types.enum';
import { JobPostingOptionalField } from '@app/modules/applicant-tracker/interfaces/optional-field.interface';
import { ApplicantOptionalFieldService } from '@app/modules/applicant-tracker/services';
import { AuthService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { Translatable } from '@app/types/translatable.type';
import { ShowView } from '@common/views';
import { JobPosting } from '@models/recruiting/job-posting.model';
import { Question } from '@models/recruiting/question.model';
import { SettingsIntegrationService } from '@settings/services';

@Component({
    templateUrl: './settings-show.template.html',
    styleUrls: ['./settings-show.style.scss'],
})
export class SettingsShowView extends ShowView implements OnInit {
    @ViewChild('confirmDeleteDocumentDialog', { static: true })
    confirmDeleteDocumentDialog: DeprecatedConfirmationDialogComponent;
    @ViewChild('attachmentForm') attachmentForm: NgForm;
    isLoading = true;
    jobPosting: JobPosting;
    initialKeywords: Chippable[] = [];
    autoArchivingQuestion: Question = new Question();
    additionalQuestion: { label: string; type: 'input' | 'number' | 'textarea' | 'date'; required: boolean } = {
        label: '',
        type: 'input',
        required: false,
    };
    jobAttachments: JobAttachment[] = [];
    jobAttachmentTypes = JobAttachmentTypes;

    atsAdditionalQuestions: Question[] = [];
    atsAutoArchivingQuestions: Question[] = [];

    // Used for when a change is made on the attachment section
    jobAttachmentChange = false;
    deleteModalMessage: Translatable;
    isAdditionalQuestionDialogOpen = false;
    hasConsentFlag = false;

    constructor(
        private notify: NotifyService,
        private auth: AuthService,
        public route: ActivatedRoute,
        private element: ElementRef,
        private integrationService: SettingsIntegrationService,
        private applicantOptionalFieldService: ApplicantOptionalFieldService,
        private featureService: FeatureService
    ) {
        super();
        this.jobPosting = this.route.snapshot.data.models.get('jobPosting');

        this.atsAdditionalQuestions = this.jobPosting.atsAdditionalQuestions;
        this.atsAutoArchivingQuestions = this.jobPosting.autoArchivingQuestions;
    }

    async ngOnInit(): Promise<void> {
        this.initialKeywords = this.jobPosting.autoScoreKeywords.map((keyword) => ({
            chipLabel: keyword,
        }));

        this.hasConsentFlag = await this.featureService.has(FeatureFlag.atsApplicantConsent);
        this.jobAttachments = this.jobPosting.jobAttachments;
        this.isLoading = false;
    }

    refreshTable(): void {
        this.refreshJobWithSpinner();
    }

    async getJobAttachments(): Promise<void> {
        const [jobAttachments] = await JobAttachment.where('atsJobId', this.jobPosting.id).get();
        this.jobAttachments = jobAttachments;
    }

    addJobAttachment(): void {
        this.jobAttachments.push(
            new JobAttachment({
                isRequired: false,
                atsJobId: this.jobPosting.id,
            })
        );
    }

    async removeJobAttachment(jobAttachment: JobAttachment): Promise<void> {
        let attachmentTypeName = '';

        if (!jobAttachment.jobAttachmentTypeName) {
            this.jobAttachments = this.jobAttachments.filter((item: JobAttachment) => jobAttachment.id !== item.id);
            this.onJobAttachmentChange();
            this.notify.success('applicant-tracker.jobAttachmentDeleted');
            return;
        }
        attachmentTypeName =
            jobAttachment.jobAttachmentType === JobAttachmentTypes.Text
                ? 'link'
                : jobAttachment.jobAttachmentTypeName.toLowerCase();

        this.deleteModalMessage = {
            key: 'applicant-tracker.areYouSureYouWantToDeleteAttachmentName',
            params: {
                attachmentTypeName: attachmentTypeName,
                jobAttachmentName: jobAttachment.name,
            },
        };
        this.confirmDeleteDocumentDialog.show().then(async (confirm) => {
            if (!confirm) {
                return;
            }
            this.isLoading = true;
            try {
                if (jobAttachment.id) {
                    await jobAttachment.delete();
                }
                this.getJobAttachments();
                this.jobAttachments = this.jobAttachments.filter((item: JobAttachment) => jobAttachment.id !== item.id);
                this.onJobAttachmentChange();
                this.isLoading = false;
                this.notify.success('applicant-tracker.jobAttachmentDeleted');
            } catch (err) {
                this.notify.error(err);
                this.isLoading = false;
            }
        });
    }

    onJobAttachmentChange(): void {
        this.jobAttachmentChange = true;
    }

    async onSaveJobAttachments(): Promise<void> {
        if (!this.submitAndValidate()) {
            return;
        }
        this.isLoading = true;

        try {
            await Promise.all(
                this.jobAttachments.map(async (jobAttachment: JobAttachment) => {
                    if (jobAttachment.isDirty()) {
                        return jobAttachment.save();
                    }
                })
            );
            this.notify.success('applicant-tracker.jobAttachmentsSaved');
            this.isLoading = false;
        } catch (err) {
            this.notify.error(err);
            this.isLoading = false;
        }
    }

    submitAndValidate(): boolean {
        this.attachmentForm.onSubmit(null);
        return this.attachmentForm.form.valid;
    }

    onSave(): void {
        if (!this.jobPosting.isDirty()) {
            return;
        }

        this.isLoading = true;

        this.jobPosting
            .param('company', this.auth.company.id)
            .save()
            .then(() => {
                this.notify.success('applicant-tracker.jobPostingSaved');
                this.isLoading = false;
            })
            .catch((err) => {
                this.notify.error(err.message || 'applicant-tracker.couldNotSaveJobPosting');
                this.isLoading = false;
            });
    }

    async refreshJob(): Promise<void> {
        try {
            const jobPosting = await JobPosting.param('company', this.auth.company.id)
                .with([
                    'applicants',
                    'hiringTeam',
                    'hiringTeam.account',
                    'jobFunnels',
                    'jobFunnels.applicants',
                    'applicantOptionalFields',
                    'autoArchiveQuestions',
                    'additionalQuestions',
                ])
                .find(this.jobPosting.id);

            this.jobPosting = jobPosting;
            this.atsAdditionalQuestions = [...this.jobPosting.atsAdditionalQuestions];
            this.atsAutoArchivingQuestions = [...this.jobPosting.autoArchivingQuestions];
        } catch (err) {
            this.notify.error(err.message || 'applicant-tracker.couldNotRefreshJobPosting');
        }
    }

    async refreshJobWithSpinner(): Promise<void> {
        this.isLoading = true;
        this.refreshJob();
        this.isLoading = false;
    }

    onCancel(): void {
        this.jobPosting.revert();
    }

    onDelete(): void {}

    /**
     * Attach the question to the job posting and clear the form
     */
    onAddAutoArchivingQuestion(): void {
        if (!this.autoArchivingQuestion.question) {
            return;
        }

        this.isLoading = true;

        this.autoArchivingQuestion.isAutoArchive = true;
        this.autoArchivingQuestion.isRequired = true;
        this.autoArchivingQuestion.jobId = this.jobPosting.id;

        this.autoArchivingQuestion
            .save()
            .then(() => {
                this.refreshJobWithSpinner();
            })
            .catch((err) => this.notify.error(err.message || 'applicant-tracker.couldNotSaveQuestion'));

        this.autoArchivingQuestion = new Question();
    }

    onDeleteAutoArchivingQuestion(question: Question): void {
        this.isLoading = true;
        question
            .param('company', this.auth.company.id)
            .param('job', this.jobPosting.id)
            .delete()
            .then(() => {
                this.refreshJobWithSpinner();
            })
            .catch((err) => this.notify.error(err.message || 'applicant-tracker.couldNotDeleteQuestion'));
    }

    /**
     * Attach the question to the job posting and clear the form
     */
    onAddAdditionalQuestion(): void {
        if (!this.additionalQuestion.label) {
            return;
        }

        if (!this.jobPosting.additionalQuestions) {
            this.jobPosting.additionalQuestions = [];
        }

        this.jobPosting.additionalQuestions.push(this.additionalQuestion);
        this.jobPosting
            .param('company', this.auth.company.id)
            .save()
            .catch((err) => this.notify.error(err.message || 'applicant-tracker.couldNotSaveQuestion'));

        this.additionalQuestion = {
            label: '',
            type: 'input',
            required: false,
        };
        ``;
        $(this.element.nativeElement).find('.ui.dropdown').dropdown('set selected', 'input');
    }

    onDeleteAdditionalQuestion(questionIndex: number): void {
        this.jobPosting.additionalQuestions = this.jobPosting.additionalQuestions.filter(
            (_question, index) => questionIndex !== index
        );

        this.jobPosting
            .param('company', this.auth.company.id)
            .save()
            .catch((err) => this.notify.error(err.message || 'applicant-tracker.couldNotSaveQuestion'));
    }

    onChangeKeywords(autoScoreKeywords: Chippable[]): void {
        this.jobPosting.autoScoreKeywords = autoScoreKeywords.map((keyword) => keyword.chipLabel);

        this.jobPosting
            .param('company', this.auth.company.id)
            .save()
            .catch((err) => this.notify.error(err.message || 'applicant-tracker.couldNotSaveAutoscoreKeywords'));
    }

    async onPause(pause: boolean): Promise<void> {
        try {
            this.isLoading = true;
            this.jobPosting = await this.integrationService.pauseJobIntegration(this.jobPosting.id, pause);
            this.notify.success({
                key: 'applicant-tracker.applicants.jobPostingHasBeenActionSuccessfully',
                params: { action: pause ? 'paused' : 'unpaused' },
            });
        } catch (err) {
            this.notify.error(err);
        }

        this.isLoading = false;
    }

    async onSelectOptionalField(fields: JobPostingOptionalField[]): Promise<void> {
        this.applicantOptionalFieldService.attachAndDetachToJobPosting(this.jobPosting, fields);

        try {
            await this.jobPosting.save();
            this.notify.success('applicant-tracker.successfullySaved');

            // Need to refresh he with a request since our update call does not give a updated model
            this.refreshJob();
        } catch (e) {
            this.notify.error(e);
        }
    }
}
